import React, { useEffect, useRef, useState } from 'react';
import { Alert, Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useOpenTestSession } from 'app/concierge/hooks/useOpenTestSession';
import ModelCard from './ModelCard';
import { IKgModelCard, IKgTransitionResponse, KgCardAction } from './types';

export interface ConciergeKgModelCardsProps extends FormControlProps {
    models: IKgModelCard[];
    entityId: number;
    actionUrl: string;
    runStatusUrl?: string;
    testSessionUrl?: string;
    translationNS?: string;
}

// "A", "A and B", "A, B and C" (spec §5)
const listNames = (names: string[], and: string): string =>
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;

export default function ConciergeKgModelCards({
    controlProps,
}: FormElementControlPropsType<ConciergeKgModelCardsProps>) {
    const { t } = useBundleTranslation([controlProps.translationNS ?? 'components/common/concierge-kg-model-cards']);
    const openTestSession = useOpenTestSession();

    const [cards, setCards] = useState<IKgModelCard[]>(controlProps.models ?? []);
    // The publish toast fires on a state flip observed by the poller; the ref keeps the previous
    // list without making the effect re-run on its own write.
    const prevCardsRef = useRef<IKgModelCard[]>(controlProps.models ?? []);

    const applyCards = (next: IKgModelCard[]) => {
        const prev = prevCardsRef.current;
        next.forEach((card) => {
            const before = prev.find((p) => p.identity_id === card.identity_id);
            if (before?.state === 'profiling' && card.state === 'available') {
                toast('success', t('toast_published_clean', { model: card.name }));
            }
            if (before?.state === 'review_required' && card.state === 'available') {
                toast('success', t('toast_published_reviewed', { model: card.name }));
            }
        });
        prevCardsRef.current = next;
        setCards(next);
    };

    // Poll while a run is in flight, and while a review card sits at zero open items — its
    // publish settles server-side and the flip should land without a reload.
    const hasProfiling = cards.some(
        (card) => card.state === 'profiling' || (card.state === 'review_required' && card.open_item_count === 0),
    );
    const cardsQuery = useQuery<IKgModelCard[]>({
        queryKey: ['conciergeKgCards', controlProps.entityId],
        queryFn: async () => {
            const response = await instance.get(controlProps.actionUrl);
            return response.data.data.concierge_kg_models as IKgModelCard[];
        },
        enabled: hasProfiling,
        refetchInterval: 5000,
        refetchIntervalInBackground: true,
    });
    const polledCards = cardsQuery.data;
    useEffect(() => {
        if (polledCards) {
            applyCards(polledCards);
        }
    }, [polledCards]);

    const mutation = useMutation({
        mutationFn: ({ card, action }: { card: IKgModelCard; action: KgCardAction }) =>
            instance.post<{ status: string; data: IKgTransitionResponse }>(
                `${controlProps.actionUrl}/${card.identity_id}/${action}`,
            ),
        onSuccess: (response, { card, action }) => {
            const data = response.data.data;
            if (action === 'add') {
                toast(
                    'success',
                    card.incremental
                        ? t('toast_add_incremental', { model: card.name })
                        : t('toast_add_first', { model: card.name }),
                );
            }
            if (action === 'remove') {
                const keptFor = data.kept_for ?? [];
                toast(
                    'success',
                    keptFor.length > 0
                        ? t('toast_removed_shared', {
                              model: card.name,
                              reports: listNames(keptFor, t('names_and')),
                          })
                        : t('toast_removed_last', { model: card.name }),
                );
            }
            applyCards(data.concierge_kg_models);
        },
        onError: (error: any) => {
            toast('error', error?.response?.data?.data?.message ?? t('action_failed'));
        },
    });

    if (cards.length === 0) {
        return null;
    }

    const noun = cards[0].model_term;
    const pendingCardId = mutation.isPending ? mutation.variables?.card.identity_id : null;

    return (
        <Stack spacing={1.5} data-test="concierge_kg_model_cards">
            {cards.length >= 2 && (
                <Alert severity="info" icon={false} data-test="concierge_kg_intro_note">
                    {t('intro_note', {
                        count: cards.length,
                        noun,
                        nounPlural: t(`noun_plural_${cards[0].bi_tool === 'tableau' ? 'tableau' : 'model'}`),
                    })}
                </Alert>
            )}
            {cards.map((card) => (
                <ModelCard
                    key={card.identity_id}
                    card={card}
                    pending={pendingCardId === card.identity_id}
                    t={t}
                    onAction={(actedCard, action) => mutation.mutate({ card: actedCard, action })}
                    onOpenTestSession={(testedCard) =>
                        openTestSession({
                            modelObjectUid: testedCard.test_session.model_object_uid,
                            elementId: testedCard.test_session.element_id,
                        })
                    }
                />
            ))}
        </Stack>
    );
}

export class ConciergeKgModelCardsBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeKgModelCardsProps {
        return {
            ...this.controlProps,
            models: this.elementProps.componentProps?.models ?? [],
            entityId: this.elementProps.componentProps?.entityId,
            actionUrl: this.elementProps.componentProps?.actionUrl,
            runStatusUrl: this.elementProps.componentProps?.runStatusUrl,
            testSessionUrl: this.elementProps.componentProps?.testSessionUrl,
            translationNS: this.elementProps.componentProps?.translationNS,
        };
    }
}
